import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { WidgetRendererComponent } from '../widget-renderer/widget-renderer.component';
import { GlobalFilterComponent } from '@shared-lib/global-components/global-filter/global-filter.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { DashboardTitleService } from '@shared-lib/shared-services/dashboard-title.service';
import { PlatformService } from '@shared-lib/shared-services/platform.service.service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import * as echarts from 'echarts';
import { ThemeService } from '@shared-lib/shared-services/theme.service';
import { LayoutService } from '@shared-lib/shared-services/layout.service';
import { CapitalCasePipe } from '@shared-lib/pipes/capital-case.pipe';

interface GridItem {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW: number;
  maxW: number;
  minH: number;
  maxH: number;
  locked: boolean;
}

interface WidgetRec {
  id: string;
  widget: any;
}

interface Interaction {
  mode: 'drag' | 'resize';
  id: string;
  corner?: 'nw' | 'ne' | 'sw' | 'se';
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
}

interface FocusModeSettings {
  enabled: boolean;
  includeTypes: Set<string>;
  excludeTypes: Set<string>;
}

interface TooltipSnapshotEntry {
  appendToBody?: boolean;
  appendTo?: unknown;
}

interface CompactViewportSnapshot {
  width: number;
  height: number;
  scale: number;
  requiredHeight: number;
}

const BPS: Record<string, number> = { xl: 1536, lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const COLS: Record<string, number> = { xl: 12, lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
type DashboardNamePosition = 'top' | 'below';
const NON_FOCUS_WIDGET_TYPES = new Set<string>([
  'slider',
  'input-box',
  'configurable-form',
  'editable-table',
  'table',
  'dynamic-edit-table',
  'upload-file',
  'notes',
  'static-screen',
  'multiselect-toggle',
  'generic-table',
  'table-generic',
  'number-stats',
  'numberstatscomponent',
  'range-bidirectional-slider',
  'entity-table',
  'entity-form',
  'button',
  'landing-module-card',
]);

@Component({
  selector: 'app-dashboard',
  imports: [
    HttpClientModule,
    WidgetRendererComponent,
    CommonModule,
    GlobalFilterComponent,
    CapitalCasePipe,
    MatTooltipModule,
  ],
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly COMPACT_BASELINE_BROWSER_ZOOM_MAX = 1.02;
  private static readonly COMPACT_EXIT_MARGIN_PX = 24;
  private static readonly COMPACT_ENTER_MARGIN_PX = 72;

  @ViewChild('dashboardContent') dashboardContentRef?: ElementRef<HTMLElement>;
  @ViewChild('gridContainer') gridContainerRef?: ElementRef<HTMLElement>;
  @HostBinding('class.no-scroll') get noScrollClass() {
    return !this.responsiveLayoutService.effectiveScreenFloating;
  }
  @HostBinding('class.focus-active') get focusActiveClass() {
    return !!this.focusedWidgetId;
  }

  configurations: any = {};
  screenFloating: boolean = true;
  configFileName!: string;
  sourceKey = 'dashboard';
  widgets: WidgetRec[] = [];
  widgetMap = new Map<string, WidgetRec>();
  layouts: Record<string, GridItem[]> = {};
  currentBp = 'lg';
  containerWidth = 0;
  activeDragId: string | null = null;
  activeResizeId: string | null = null;
  focusedWidgetId: string | null = null;
  focusMode: FocusModeSettings = {
    enabled: false,
    includeTypes: new Set<string>(),
    excludeTypes: new Set<string>(),
  };
  editMode = false;
  layoutEditEnabled = false;
  grid = {
    version: 1,
    breakpoints: { ...BPS },
    columns: { ...COLS },
    rowHeight: 72,
    gap: 12,
    draggable: true,
    resizable: true,
    persist: true,
    persistKey: '',
    defaultW: 4,
    defaultH: 4,
    defaultMinW: 2,
    defaultMinH: 2,
    defaultMaxH: 16,
    rawLayouts: {} as Record<string, Array<Record<string, unknown>>>,
  };

  private subs = new Subscription();
  private layoutService = inject(LayoutService);
  /** Untouched response from the most recent loadCharts/loadDashboardFromApi.
   *  Held so the LayoutService can reshape its pageContent on layout switches
   *  without re-fetching the dashboard. */
  private originalDashboardConfig: any = null;
  /** JSON snapshot of the last applied pageContent — lets the layout
   *  subscription skip re-renders when the resolved layout is unchanged. */
  private appliedLayoutSignature: string | null = null;
  private interaction?: Interaction;
  private interactionTarget?: HTMLElement;
  private queued: { x: number; y: number } | null = null;
  private raf = false;
  private gridObserver?: ResizeObserver;
  private tooltipAppendSnapshot = new WeakMap<HTMLElement, TooltipSnapshotEntry[]>();
  private focusTooltipSyncTimer?: ReturnType<typeof setInterval>;
  private compactFitRafId: number | null = null;
  private compactScrollLock = false;
  private compactScrollLockSnapshot: CompactViewportSnapshot | null = null;
  private navFiltersApplied = false;

  get effectiveScreenFloating(): boolean {
    return this.responsiveLayoutService.effectiveScreenFloating;
  }

  get isInteracting(): boolean {
    return !!this.interaction;
  }

  get hasFocusWidget(): boolean {
    return !!this.focusedWidgetId;
  }

  get orderedBps(): string[] {
    return Object.entries(this.grid.breakpoints).sort((a, b) => b[1] - a[1]).map(([k]) => k);
  }

  get cols(): number {
    return this.grid.columns[this.currentBp] ?? 12;
  }

  get gridStyle(): Record<string, string> {
    return {
      'grid-template-columns': `repeat(${this.cols}, minmax(0, 1fr))`,
      'grid-auto-rows': `${this.grid.rowHeight}px`,
      // Custom Theme tab's "Widget spacing" choice writes --dvm-widget-gap;
      // when unset, fall back to the dashboard JSON's own gap value.
      gap: `var(--dvm-widget-gap, ${this.grid.gap}px)`,
    };
  }

  get dashboardNamePosition(): DashboardNamePosition {
    return this.normalizeDashboardNamePosition(
      this.configurations?.dashboardNamePosition ??
      this.configurations?.['dashboardNamePosition'] ??
      this.configurations?.['dashboard-name-position']
    );
  }

  get showDashboardNameOnScreen(): boolean {
    return this.dashboardNamePosition === 'below';
  }

  get showDashboardNameInNavbar(): boolean {
    return this.dashboardNamePosition === 'top';
  }

  get viewItems(): Array<{ item: GridItem; rec: WidgetRec }> {
    const l = this.layouts[this.currentBp] ?? [];
    return l
      .map((item) => {
        const rec = this.widgetMap.get(item.id);
        return rec ? { item, rec } : null;
      })
      .filter((x): x is { item: GridItem; rec: WidgetRec } => !!x);
  }

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    public configService: ConfigService,
    private route: ActivatedRoute,
    private globalFilterService: GlobalFilterService,
    private platformService: PlatformService,
    private responsiveLayoutService: ResponsiveLayoutService,
    private themeService: ThemeService,
    private dashboardTitleService: DashboardTitleService,
  ) { }

  ngOnInit(): void {
    this.responsiveLayoutService.init();

    this.subs.add(
      this.responsiveLayoutService.reflow$.subscribe(() => {
        if (this.configurations?.pageContent?.length) {
          this.applyWidgetHeightsInPlace(this.configurations);
        }
        this.queueCompactFitMeasurement();
      })
    );

    // Load the layout manifest in parallel with the dashboard config — once
    // it arrives, re-resolve the active layout so a saved selection (from
    // localStorage) is honored on first paint, not just after the user picks.
    this.subs.add(
      this.layoutService.loadManifest().subscribe(() => this.maybeReapplyLayout())
    );

    // React to layout switches from the Theme Options dialog: when the user
    // hits "Apply & Preview" or "Clear Layout", reshape the current dashboard
    // and re-run the grid pipeline. The first emission (BehaviorSubject seed)
    // is a no-op because originalDashboardConfig isn't loaded yet.
    this.subs.add(
      this.layoutService.activeLayoutId$.subscribe(() => this.maybeReapplyLayout())
    );

    // Seed filters from navigation before any widget API call starts.
    this.globalFilterService.updateAllFilters(this.buildInitialFiltersFromNavigation());
    this.globalFilterService.activeFilter = null;
    this.globalFilterService.resetFilterSelection('customer_spec');

    const data = this.route.snapshot.data;
    if (data['configFile']) {
      this.configFileName = data['configFile'];
      this.sourceKey = this.configFileName;
      this.loadCharts();
    } else {
      const appId = this.route.snapshot.data['dvmWorkflowAppId'];
      if (appId) {
        this.sourceKey = appId;
        this.loadDashboardFromApi(environment.PROJECT_ID, appId);
      }
    }
  }

  ngAfterViewInit(): void {
    this.responsiveLayoutService.startChartAutoResize();
    this.gridObserver = new ResizeObserver(() => {
      this.onViewportChange();
      this.queueCompactFitMeasurement();
    });
    if (this.dashboardContentRef?.nativeElement) this.gridObserver.observe(this.dashboardContentRef.nativeElement);
    if (this.gridContainerRef?.nativeElement) this.gridObserver.observe(this.gridContainerRef.nativeElement);
    this.onViewportChange();
    this.queueCompactFitMeasurement();
  }

  ngOnDestroy(): void {
    this.finishInteraction(true);
    this.gridObserver?.disconnect();
    if (this.compactFitRafId !== null) {
      cancelAnimationFrame(this.compactFitRafId);
      this.compactFitRafId = null;
    }

    // ✅ from other branch
    this.themeService.clearDashboardOverrides();

    this.responsiveLayoutService.stopChartAutoResize();
    this.subs.unsubscribe();

    this.stopFocusTooltipSync();
    void this.exitFocusMode();
    this.syncFocusBodyClass();

    this.responsiveLayoutService.setScreenFloating(true);
    this.responsiveLayoutService.setCompactLayoutFitOverride(null);
    this.compactScrollLock = false;
    this.compactScrollLockSnapshot = null;

    this.globalFilterService.clearAllFilters();
    this.globalFilterService.activeFilter = null;
    this.dashboardTitleService.clearDashboardTitle();
  }

  /**
   * Seed GlobalFilterService from navigation query params + router state payload.
   * This enables cross-page payload handoff (e.g. navigateAction.stateKey payload)
   * without requiring each destination widget/component to read Router state.
   */
  private buildInitialFiltersFromNavigation(): {
    queryParams: Record<string, any>;
    requestParams: Record<string, any>;
    widgetId: string | null;
    filterListener?: string[];
    sourceWidgetId?: string | null;
  } {
    const queryParams = { ...(this.route?.snapshot?.queryParams || {}) } as Record<string, any>;
    const requestParams: Record<string, any> = {};
    const navState: any = (window.history?.state || {}) as any;
    let filterListener: string[] | undefined;
    let sourceWidgetId: string | null = null;

    // Common pattern used in dashboard configs:
    // navigateAction: { stateKey: "crossNavPayload", payload: { requestData: {...} } }
    const crossNavPayload = navState?.crossNavPayload;
    if (crossNavPayload && typeof crossNavPayload === 'object') {
      if (crossNavPayload.requestData && typeof crossNavPayload.requestData === 'object') {
        Object.assign(requestParams, crossNavPayload.requestData);
      }
      if (crossNavPayload.queryParams && typeof crossNavPayload.queryParams === 'object') {
        Object.assign(queryParams, crossNavPayload.queryParams);
      }
      if (crossNavPayload.requestParams && typeof crossNavPayload.requestParams === 'object') {
        Object.assign(requestParams, crossNavPayload.requestParams);
      }
      Object.assign(requestParams, this.extractLooseRequestParams(crossNavPayload));
      filterListener = this.normalizeNavigationListener(
        crossNavPayload.filterListener ??
        crossNavPayload.listenerWidget ??
        crossNavPayload.listenerWidgets ??
        crossNavPayload.ListenerWidget ??
        crossNavPayload.ListenerWidgets ??
        crossNavPayload.listnerWidget ??
        crossNavPayload.listnerWidgets ??
        crossNavPayload.ListnerWidget ??
        crossNavPayload.ListnerWidgets ??
        crossNavPayload.Listnerwidget,
      );
      sourceWidgetId = this.firstString(crossNavPayload.sourceWidgetId, queryParams['sourceWidgetId']);
    }

    // Also support direct state usage for flexibility.
    if (navState?.requestParams && typeof navState.requestParams === 'object') {
      Object.assign(requestParams, navState.requestParams);
    }
    if (navState?.queryParams && typeof navState.queryParams === 'object') {
      Object.assign(queryParams, navState.queryParams);
    }
    const directListener = this.normalizeNavigationListener(
      navState?.filterListener ??
      navState?.listenerWidget ??
      navState?.listenerWidgets ??
      navState?.ListenerWidget ??
      navState?.ListenerWidgets ??
      navState?.listnerWidget ??
      navState?.listnerWidgets ??
      navState?.ListnerWidget ??
      navState?.ListnerWidgets ??
      navState?.Listnerwidget,
    );
    if (directListener?.length) {
      filterListener = directListener;
    }
    sourceWidgetId = this.firstString(sourceWidgetId, navState?.sourceWidgetId, queryParams['sourceWidgetId']);

    return {
      queryParams,
      requestParams,
      widgetId: null,
      ...(filterListener?.length ? { filterListener } : {}),
      ...(sourceWidgetId ? { sourceWidgetId } : {}),
    };
  }

  private normalizeNavigationListener(rawValue: unknown): string[] | undefined {
    const rawList = Array.isArray(rawValue)
      ? rawValue
      : typeof rawValue === 'string'
        ? rawValue.split(',')
        : [];

    const listeners = rawList
      .map((item) => String(item || '').trim())
      .filter((item) => !!item);

    return listeners.length ? listeners : undefined;
  }

  private extractLooseRequestParams(payload: any): Record<string, any> {
    if (!payload || typeof payload !== 'object') return {};

    const hasStructuredRequestParams =
      (payload.requestData && typeof payload.requestData === 'object') ||
      (payload.requestParams && typeof payload.requestParams === 'object');

    if (hasStructuredRequestParams) return {};

    const reservedKeys = new Set([
      'queryParams',
      'requestParams',
      'requestData',
      'filterListener',
      'listenerWidget',
      'listenerWidgets',
      'ListenerWidget',
      'ListenerWidgets',
      'listnerWidget',
      'listnerWidgets',
      'ListnerWidget',
      'ListnerWidgets',
      'Listnerwidget',
      'sourceWidgetId',
    ]);
    const looseParams: Record<string, any> = {};

    for (const [key, value] of Object.entries(payload)) {
      if (reservedKeys.has(key) || value === undefined || value === null || value === '') {
        continue;
      }
      looseParams[key] = value;
    }

    return looseParams;
  }

  private firstString(...values: unknown[]): string | null {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return null;
  }

  private normalizeDashboardNamePosition(value: unknown): DashboardNamePosition {
    return typeof value === 'string' && value.trim().toLowerCase() === 'top'
      ? 'top'
      : 'below';
  }

  private resolveDashboardTitle(config: any): string {
    return String(
      config?.['dashboard-name'] ??
      config?.dashboardName ??
      config?.dashboard_name ??
      ''
    ).trim();
  }

  private setDashboardTitleContext(config: any): void {
    this.dashboardTitleService.setDashboardTitle(
      this.resolveDashboardTitle(config),
      this.normalizeDashboardNamePosition(
        config?.dashboardNamePosition ??
        config?.['dashboardNamePosition'] ??
        config?.['dashboard-name-position']
      ),
    );
  }

  loadDashboardFromApi(projectId: string, appId: string) {
    const configSub = this.platformService
      .getDashboardConfigFromApi(projectId, appId)
      .pipe(
        switchMap((response: any) => {
          if (!response?.length) return of(null);

          const config = response[0].dvm_workflow_map;
          // Priority: user's saved active theme (Save Theme button) > current
          // in-memory theme (covers a still-pending preview not yet saved) >
          // light/dark toggle > dashboard config > env default > 'default'.
          // Without the saved-name check, navigating away and back would
          // revert a saved theme to the dashboard JSON's `theme` field —
          // because `getUserPreference()` reads `dvm.theme` (light/dark
          // toggle), not `dvm.theme.activeName` (Save Theme target).
          const themeName =
            this.themeService.getSavedThemeName() ||
            this.themeService.currentThemeName ||
            this.themeService.getUserPreference() ||
            config?.theme || (environment as any).THEME_NAME || 'default';

          return this.themeService.loadTheme(themeName).pipe(
            tap((theme) => {
              this.themeService.initFromLoaded(themeName, theme);

              if (config?.themeOverrides) {
                this.themeService.setDashboardOverrides(config.themeOverrides);
              }
            }),
            switchMap(() => of(config))
          );
        })
      )
      .subscribe({
        next: (config: any) => {
          if (!config) return;
          this.originalDashboardConfig = config;
          // Per-dashboard layout: tell the service which dashboard is active
          // (sourceKey = appId here) and what its JSON-declared default is,
          // so the resolved layout reflects this dashboard's saved pick or
          // its `layoutId` field. Must precede applyDashboardWithLayout —
          // the resulting activeLayoutId$ emission also fires the layout
          // subscription, but the signature check makes the second pass a
          // no-op.
          this.layoutService.setActiveDashboardKey(
            this.sourceKey,
            config?.layoutId ?? null,
          );
          this.applyDashboardWithLayout(config);
        },
        error: (err) =>
          console.error('Error loading dashboard config:', err),
      });

    this.subs.add(configSub); // ✅ fixed
  }

  loadCharts() {
    const configSub = this.configService
      .getDashboardConfig(this.configFileName)
      .pipe(
        switchMap((response: any) => {
          // Priority: user's saved active theme (Save Theme button) > current
          // in-memory theme (covers a still-pending preview not yet saved) >
          // light/dark toggle > dashboard config > env default > 'default'.
          const themeName =
            this.themeService.getSavedThemeName() ||
            this.themeService.currentThemeName ||
            this.themeService.getUserPreference() ||
            response?.theme || (environment as any).THEME_NAME || 'default';

          return this.themeService.loadTheme(themeName).pipe(
            tap((theme) => {
              this.themeService.initFromLoaded(themeName, theme);

              if (response?.themeOverrides) {
                this.themeService.setDashboardOverrides(
                  response.themeOverrides
                );
              }
            }),
            switchMap(() => of(response))
          );
        })
      )
      .subscribe((response) => {
        this.originalDashboardConfig = response;
        // Per-dashboard layout — see loadDashboardFromApi for the rationale.
        this.layoutService.setActiveDashboardKey(
          this.sourceKey,
          response?.layoutId ?? null,
        );
        this.applyDashboardWithLayout(response);
      });

    this.subs.add(configSub); //  fixed
  }

  /**
   * Resolve the effective dashboard config (reshape via active Custom Layout
   * if one is selected, else use the dashboard's own pageContent), then run
   * the height + screen-floating + grid + responsive pipeline.
   *
   * Skips the work if the resolved layout signature matches what's already
   * applied — avoids unnecessary chart re-renders on benign emissions
   * (BehaviorSubject seed, manifest-loaded callback firing identical state).
   */
  private applyDashboardWithLayout(config: any): void {
    const activeLayoutId = this.layoutService.getActiveLayoutId();
    const reshaped = this.layoutService.reshapePageContent(
      config?.pageContent,
      activeLayoutId,
    );
    const effective = { ...config, pageContent: reshaped };
    const signature = JSON.stringify(reshaped ?? null);
    this.setDashboardTitleContext(effective);
    if (signature === this.appliedLayoutSignature) return;
    this.appliedLayoutSignature = signature;

    //  HEIGHT SYSTEM
    this.configurations = this.applyWidgetHeights(effective);
    this.applyNavigationFiltersToGlobalService(this.configurations);

    //  SCREEN FLOATING
    this.applyScreenFloating(this.configurations);

    //  GRID ENGINE (CRITICAL)
    this.applyConfig();

    //  RESPONSIVE SERVICE
    this.responsiveLayoutService.setRowCount(
      this.configurations?.pageContent?.length || 0,
    );

    // ECharts doesn't observe its container — without an explicit resize()
    // signal after a layout/reshape, charts keep stale dimensions and
    // visibly misbehave. RAF lets the new grid styles flush before reflow.
    requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
  }

  /**
   * Push navigation-carried values into GlobalFilterService using dashboard
   * filter metadata so charts receive them as request/query params on first load.
   */
  private applyNavigationFiltersToGlobalService(config: any): void {
    if (this.navFiltersApplied) return;

    const fromNav = this.buildInitialFiltersFromNavigation();
    const dashboardFilters = config?.filters?.content || [];
    console.log('[DVM NAV DEBUG] fromNav:', fromNav);
    console.log('[DVM NAV DEBUG] dashboard filter keys:', (dashboardFilters || []).map((f: any) => f?.key));

    const queryParams = { ...(fromNav.queryParams || {}) } as Record<string, any>;
    const hasNavigationListeners = !!fromNav.filterListener?.length;
    const navigationRequestParams = hasNavigationListeners
      ? { ...(fromNav.requestParams || {}) }
      : {};
    const requestParams = hasNavigationListeners
      ? {}
      : ({ ...(fromNav.requestParams || {}) } as Record<string, any>);

    if (dashboardFilters?.length) {
      for (const filter of dashboardFilters) {
        const key = String(filter?.key || '').trim();
        if (!key) continue;
        const raw = queryParams[key] ?? requestParams[key];
        if (raw === undefined || raw === null || raw === '') continue;

        const normalized = Array.isArray(raw)
          ? raw
          : (filter?.isMultiSelect ? [raw] : raw);

        if (filter?.isQueryParam) {
          queryParams[key] = normalized;
        } else {
          requestParams[key] = normalized;
        }
      }
    }

    const nextFilters = {
      queryParams,
      requestParams,
      widgetId: null,
      ...(hasNavigationListeners ? { navigationRequestParams } : {}),
      ...(hasNavigationListeners ? { navigationFilterListener: fromNav.filterListener } : {}),
      ...(fromNav.sourceWidgetId ? { navigationSourceWidgetId: fromNav.sourceWidgetId } : {}),
    };

    this.globalFilterService.updateAllFilters(nextFilters);
    console.log('[DVM NAV DEBUG] applied global filters:', {
      queryParams,
      requestParams,
      navigationRequestParams,
      filterListener: fromNav.filterListener,
    });
    this.navFiltersApplied = true;
  }

  /** Re-apply the active layout against the cached original config.
   *  No-op until the dashboard config has loaded. */
  private maybeReapplyLayout(): void {
    if (!this.originalDashboardConfig) return;
    this.applyDashboardWithLayout(this.originalDashboardConfig);
  }

  private applyScreenFloating(config: any): void {
    // Default to true (scrolling enabled) if key is not present
    this.screenFloating = config?.screenFloating !== false;
    this.responsiveLayoutService.setScreenFloating(this.screenFloating);
    if (this.screenFloating) {
      this.responsiveLayoutService.setCompactLayoutFitOverride(null);
      this.compactScrollLock = false;
      this.compactScrollLockSnapshot = null;
    }
  }

  private applyWidgetHeights(config: any): any {
    if (!config || !Array.isArray(config.pageContent)) {
      return config;
    }

    const updatedPageContent = config.pageContent.map((row: any) => ({
      ...row,
      columns: this.normalizeColumns(row?.columns || []),
    }));

    return {
      ...config,
      pageContent: updatedPageContent,
    };
  }

  private applyWidgetHeightsInPlace(config: any): void {
    if (!config || !Array.isArray(config.pageContent)) {
      return;
    }

    for (const row of config.pageContent) {
      this.applyColumnHeightsInPlace(row?.columns || []);
    }
  }

  private applyColumnHeightsInPlace(columns: any[]): void {
    for (const column of columns) {
      if (column?.type) {
        column.height = this.resolveWidgetHeight(column);
      }

      if (Array.isArray(column?.columns)) {
        this.applyColumnHeightsInPlace(column.columns);
      }

      if (Array.isArray(column?.rows)) {
        for (const nestedRow of column.rows) {
          if (nestedRow?.type) {
            nestedRow.height = this.resolveWidgetHeight(nestedRow);
          }

          if (Array.isArray(nestedRow?.columns)) {
            this.applyColumnHeightsInPlace(nestedRow.columns);
          }

          if (Array.isArray(nestedRow?.rows)) {
            this.applyColumnHeightsInPlace(nestedRow.rows);
          }
        }
      }
    }
  }

  private normalizeColumns(columns: any[]): any[] {
    return columns.map((column: any) => {
      const updatedColumn = { ...column };

      if (updatedColumn?.type) {
        updatedColumn.height = this.resolveWidgetHeight(updatedColumn);
      }

      if (Array.isArray(updatedColumn?.columns)) {
        updatedColumn.columns = this.normalizeColumns(updatedColumn.columns);
      }

      if (Array.isArray(updatedColumn?.rows)) {
        updatedColumn.rows = updatedColumn.rows.map((nestedRow: any) => ({
          ...nestedRow,
          columns: this.normalizeColumns(nestedRow?.columns || []),
          ...(nestedRow?.type
            ? { height: this.resolveWidgetHeight(nestedRow) }
            : {}),
        }));
      }

      return updatedColumn;
    });
  }

  private resolveWidgetHeight(widget: any): string {
    return this.responsiveLayoutService.resolveWidgetHeight(widget);
  }



  @HostListener('window:resize')
  onViewportChange(): void {
    this.containerWidth = this.gridContainerRef?.nativeElement?.clientWidth || window.innerWidth;
    this.currentBp = this.resolveBp(window.innerWidth);
    if (!this.layouts[this.currentBp]?.length) this.layouts[this.currentBp] = this.buildDefaultLayout(this.currentBp);
    this.updateRowsForResponsiveService();
    this.releaseCompactScrollLockIfViewportImproved();
    this.queueCompactFitMeasurement();
  }

  @HostListener('window:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.interaction || event.pointerId !== this.interaction.pointerId) return;
    event.preventDefault();
    this.queued = { x: event.clientX, y: event.clientY };
    if (this.raf) return;
    this.raf = true;
    requestAnimationFrame(() => {
      this.raf = false;
      if (!this.interaction || !this.queued) return;
      this.applyInteraction(this.queued.x, this.queued.y);
      this.queued = null;
    });
  }

  @HostListener('window:pointerup', ['$event'])
  @HostListener('window:pointercancel', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (!this.interaction || event.pointerId !== this.interaction.pointerId) return;
    this.finishInteraction(false);
  }

  @HostListener('window:blur')
  onBlur(): void {
    if (this.interaction) this.finishInteraction(false);
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    void this.exitFocusMode();
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    const prevFocusedId = this.focusedWidgetId;
    const active = document.fullscreenElement as HTMLElement | null;
    const nextId = active?.getAttribute('data-widget-id') || null;
    if (active) {
      this.syncChartTooltipContainer(active, true);
      this.startFocusTooltipSync(active);
    } else {
      this.stopFocusTooltipSync();
    }
    if (!nextId && prevFocusedId) {
      const prevEl = this.getWidgetElement(prevFocusedId);
      if (prevEl) this.syncChartTooltipContainer(prevEl, false);
    }
    this.setFocusedWidget(nextId);
  }

  widgetStyle(item: GridItem): Record<string, string> {
    return { 'grid-column': `${item.x + 1} / span ${item.w}`, 'grid-row': `${item.y + 1} / span ${item.h}` };
  }

  label(widget: any): string {
    return String(widget?.title || widget?.widgetId || 'widget');
  }

  focusToggleIcon(id: string): string {
    return this.isFocused(id) ? 'close_fullscreen' : 'open_in_full';
  }

  focusToggleLabel(id: string, widget: any): string {
    const action = this.isFocused(id) ? 'Close focus for ' : 'Open focus for ';
    return `${action}${this.label(widget)}`;
  }

  isFocused(id: string): boolean {
    return this.focusedWidgetId === id;
  }

  canShowFocusToggle(widget: any): boolean {
    if (!this.focusMode.enabled) return false;
    const type = this.normalizeType(widget?.type);
    if (!type) return false;
    if (this.focusMode.excludeTypes.has(type)) return false;
    if (this.focusMode.includeTypes.size) return this.focusMode.includeTypes.has(type);
    return !NON_FOCUS_WIDGET_TYPES.has(type);
  }

  onFocusTogglePointerDown(event: PointerEvent): void {
    event.stopPropagation();
  }

  toggleFocus(id: string): void {
    if (this.focusedWidgetId === id) {
      void this.exitFocusMode();
      return;
    }
    void this.enterFocusMode(id);
  }

  canDrag(item: GridItem): boolean { return this.editMode && !this.hasFocusWidget && this.grid.draggable && !item.locked; }
  canResize(item: GridItem): boolean { return this.editMode && !this.hasFocusWidget && this.grid.resizable && !item.locked; }
  isDragging(id: string): boolean { return this.activeDragId === id; }
  isResizing(id: string): boolean { return this.activeResizeId === id; }

  toggleEditMode(): void {
    if (!this.layoutEditEnabled) return;

    if (this.editMode) {
      if (this.interaction) {
        this.finishInteraction(true);
      } else {
        this.writePersisted();
      }
      this.editMode = false;
      this.activeDragId = null;
      this.activeResizeId = null;
      return;
    }
    this.editMode = true;
  }

  startDrag(event: PointerEvent, item: GridItem): void { this.startInteraction(event, item, 'drag'); }
  startResize(event: PointerEvent, item: GridItem, corner: 'nw' | 'ne' | 'sw' | 'se'): void {
    this.startInteraction(event, item, 'resize', corner);
  }
  onWidgetPointerDown(event: PointerEvent, item: GridItem): void {
    if (!this.editMode || !this.canDrag(item)) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('.resize-handle') || target.closest('.focus-mode-toggle')) return;
    const host = event.currentTarget as HTMLElement | null;
    const hasHeader = !!host?.querySelector('.card-header');
    // If widget has a card header, drag starts from header only.
    if (hasHeader && !target.closest('.card-header')) return;
    this.startDrag(event, item);
  }

  private applyConfig(): void {
    this.responsiveLayoutService.setScreenFloating(this.configurations?.screenFloating !== false);
    this.widgets = this.extractWidgets(this.configurations?.pageContent ?? []);
    this.widgetMap = new Map(this.widgets.map((w) => [w.id, w]));
    this.resolveFocusModeConfig();
    if (this.focusedWidgetId) {
      const focusedRec = this.widgetMap.get(this.focusedWidgetId);
      if (!focusedRec || !this.canShowFocusToggle(focusedRec.widget)) void this.exitFocusMode();
    }
    this.resolveGridConfig();
    this.resolveLayoutEditConfig();
    this.layouts = {};
      const persisted = this.readPersisted();
    for (const bp of this.orderedBps) {
      const base = this.buildDefaultLayout(bp);
      const raw = this.parseRawLayout(this.grid.rawLayouts[bp] ?? [], bp);
      const mergedA = raw.length ? this.merge(base, raw, bp) : base;
      const mergedB = persisted?.[bp]?.length ? this.merge(mergedA, persisted[bp], bp, { preserveBaseHeight: true }) : mergedA;
      this.layouts[bp] = this.pack(mergedB, null, this.grid.columns[bp] ?? 12);
    }
    this.onViewportChange();
    this.queueCompactFitMeasurement();
  }

  private resolveGridConfig(): void {
    const c = this.configurations?.layoutConfig ?? this.configurations?.gridLayout ?? {};
    const defaultWidgetHeight =
      String(
        this.configurations?.defaultWidgetHeight ??
        this.configurations?.defaultHeight ??
        this.configurations?.defaultheight ??
        c?.defaultWidgetHeight ??
        c?.defaultheight ??
        ''
      ).trim();
    if (defaultWidgetHeight) {
      this.responsiveLayoutService.setDefaultWidgetHeight(defaultWidgetHeight);
    }
    this.grid.breakpoints = this.mapNumbers(c?.breakpoints, BPS);
    this.grid.columns = this.mapNumbers(c?.columns ?? c?.cols, COLS);
    this.grid.rowHeight = this.num(c?.rowHeight, 72);
    this.grid.gap = this.num(Array.isArray(c?.margin) ? c.margin[0] : (c?.gap ?? c?.margin), 12);
    this.grid.draggable = c?.draggable !== false;
    this.grid.resizable = c?.resizable !== false;
    this.grid.persist = c?.persist !== false;
    this.grid.persistKey = String(c?.persistKey ?? '').trim();
    this.grid.defaultW = this.num(c?.defaultWidth, 4);
    this.grid.defaultH = this.num(c?.defaultHeight, 4);
    this.grid.defaultMinW = this.num(c?.defaultMinW, 2);
    this.grid.defaultMinH = this.num(c?.defaultMinH, 2);
    this.grid.defaultMaxH = this.num(c?.defaultMaxH, 16);
    this.grid.rawLayouts = typeof c?.layouts === 'object' && c?.layouts ? c.layouts : {};
  }

  private resolveLayoutEditConfig(): void {
    const c = this.configurations?.layoutConfig ?? this.configurations?.gridLayout ?? {};
    const enabled = this.firstEnabledFlag(
      this.configurations?.enableEdit,
      this.configurations?.editable,
      this.configurations?.editMode,
      this.configurations?.layoutEdit,
      this.configurations?.layoutEditMode,
      c?.enableEdit,
      c?.editable,
      c?.edit,
      c?.editMode,
      c?.layoutEdit
    );

    this.layoutEditEnabled = enabled === true;

    if (!this.layoutEditEnabled && this.editMode) {
      if (this.interaction) this.finishInteraction(false);
      this.editMode = false;
      this.activeDragId = null;
      this.activeResizeId = null;
    }
  }

  private resolveFocusModeConfig(): void {
    const dashboardConfig = this.configurations?.focusMode
      ?? this.configurations?.enableFocusMode
      ?? this.configurations?.focus;
    const gridConfig = this.configurations?.layoutConfig?.focusMode
      ?? this.configurations?.layoutConfig?.enableFocusMode
      ?? this.configurations?.gridLayout?.focusMode
      ?? this.configurations?.gridLayout?.enableFocusMode;
    const raw = dashboardConfig ?? gridConfig;

    if (typeof raw === 'boolean') {
      this.focusMode = { enabled: raw, includeTypes: new Set<string>(), excludeTypes: new Set<string>() };
      if (!raw) void this.exitFocusMode();
      return;
    }

    if (!raw || typeof raw !== 'object') {
      this.focusMode = { enabled: false, includeTypes: new Set<string>(), excludeTypes: new Set<string>() };
      void this.exitFocusMode();
      return;
    }

    const cfg = raw as Record<string, unknown>;
    this.focusMode = {
      enabled: this.toEnabledFlag(cfg) === true,
      includeTypes: new Set<string>(this.mapStrings(cfg['includeTypes'] ?? cfg['types'] ?? cfg['chartTypes'])),
      excludeTypes: new Set<string>(this.mapStrings(cfg['excludeTypes'] ?? cfg['disabledTypes'])),
    };

    if (!this.focusMode.enabled) void this.exitFocusMode();
  }

  private async enterFocusMode(id: string): Promise<void> {
    const rec = this.widgetMap.get(id);
    if (!rec || !this.canShowFocusToggle(rec.widget)) return;

    const target = this.getWidgetElement(id);
    if (!target?.requestFullscreen) return;

    if (this.interaction) this.finishInteraction(false);
    try {
      if (document.fullscreenElement && document.fullscreenElement !== target) {
        const active = document.fullscreenElement as HTMLElement | null;
        if (active) this.syncChartTooltipContainer(active, false);
        this.stopFocusTooltipSync();
        await document.exitFullscreen();
      }
      await target.requestFullscreen();
      this.syncChartTooltipContainer(target, true);
      this.startFocusTooltipSync(target);
      this.setFocusedWidget(id);
    } catch (error) {
      console.error('Unable to enter focus mode fullscreen:', error);
      this.syncChartTooltipContainer(target, false);
      this.stopFocusTooltipSync();
      this.setFocusedWidget(null);
    }
  }

  private async exitFocusMode(): Promise<void> {
    this.stopFocusTooltipSync();
    const active = document.fullscreenElement as HTMLElement | null;
    if (active) this.syncChartTooltipContainer(active, false);
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch (error) {
      console.error('Unable to exit focus mode fullscreen:', error);
    } finally {
      this.setFocusedWidget(null);
    }
  }

  private setFocusedWidget(id: string | null): void {
    let nextId = id;
    if (nextId) {
      const rec = this.widgetMap.get(nextId);
      if (!rec || !this.canShowFocusToggle(rec.widget)) nextId = null;
    }
    if (this.focusedWidgetId === nextId) return;

    if (this.interaction) this.finishInteraction(false);
    this.focusedWidgetId = nextId;
    this.syncFocusBodyClass();
    requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
  }

  private getWidgetElement(id: string): HTMLElement | null {
    return this.gridContainerRef?.nativeElement
      ?.querySelector(`.grid-widget[data-widget-id="${id}"]`) as HTMLElement | null;
  }

  private syncChartTooltipContainer(widgetEl: HTMLElement, fullscreen: boolean, triggerReflow = true): void {
    const chartDoms = this.collectChartDoms(widgetEl);
    chartDoms.forEach((chartHost) => {
      const chart = echarts.getInstanceByDom(chartHost);
      if (!chart) return;

      const option = chart.getOption() as any;
      const tooltipList = Array.isArray(option?.tooltip)
        ? option.tooltip
        : (option?.tooltip ? [option.tooltip] : []);
      if (!tooltipList.length) return;

      if (fullscreen) {
        if (!this.tooltipAppendSnapshot.has(chartHost)) {
          this.tooltipAppendSnapshot.set(
            chartHost,
            tooltipList.map((t: any) => ({
              appendToBody: (t && typeof t === 'object' && typeof t.appendToBody === 'boolean') ? t.appendToBody : undefined,
              appendTo: (t && typeof t === 'object') ? t.appendTo : undefined,
            }))
          );
        }

        const patch = tooltipList.map((t: any) => ({
          ...(t && typeof t === 'object' ? t : {}),
          appendToBody: false,
          appendTo: widgetEl,
        }));
        chart.setOption({ tooltip: patch.length === 1 ? patch[0] : patch }, { lazyUpdate: true });
        return;
      }

      const snapshot = this.tooltipAppendSnapshot.get(chartHost);
      if (!snapshot?.length) return;
      const patch = tooltipList.map((t: any, idx: number) => {
        const snap = snapshot[idx];
        return {
          ...(t && typeof t === 'object' ? t : {}),
          appendToBody: snap?.appendToBody === true,
          appendTo: snap && 'appendTo' in snap ? snap.appendTo : null,
        };
      });
      chart.setOption({ tooltip: patch.length === 1 ? patch[0] : patch }, { lazyUpdate: true });
      this.tooltipAppendSnapshot.delete(chartHost);
    });

    if (triggerReflow) {
      requestAnimationFrame(() => this.responsiveLayoutService.triggerReflow());
    }
  }

  private collectChartDoms(widgetEl: HTMLElement): HTMLElement[] {
    const found = new Set<HTMLElement>();
    const els = widgetEl.querySelectorAll('*');
    els.forEach((n) => {
      const chart = echarts.getInstanceByDom(n as HTMLElement);
      if (chart) {
        const dom = chart.getDom() as HTMLElement;
        if (dom) found.add(dom);
      }
    });
    return [...found];
  }

  private startFocusTooltipSync(widgetEl: HTMLElement): void {
    this.stopFocusTooltipSync();
    this.focusTooltipSyncTimer = setInterval(() => {
      const active = document.fullscreenElement as HTMLElement | null;
      if (!active || active !== widgetEl) {
        this.stopFocusTooltipSync();
        return;
      }
      this.syncChartTooltipContainer(widgetEl, true, false);
    }, 300);
  }

  private stopFocusTooltipSync(): void {
    if (!this.focusTooltipSyncTimer) return;
    clearInterval(this.focusTooltipSyncTimer);
    this.focusTooltipSyncTimer = undefined;
  }

  private syncFocusBodyClass(): void {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('dashboard-focus-active', !!this.focusedWidgetId);
  }

  private startInteraction(
    event: PointerEvent,
    item: GridItem,
    mode: 'drag' | 'resize',
    corner?: 'nw' | 'ne' | 'sw' | 'se'
  ): void {
    if (event.button !== 0 || (mode === 'drag' && !this.canDrag(item)) || (mode === 'resize' && !this.canResize(item))) return;
    event.preventDefault(); event.stopPropagation();
    this.interaction = {
      mode, id: item.id, corner, pointerId: event.pointerId, startClientX: event.clientX, startClientY: event.clientY,
      startX: item.x, startY: item.y, startW: item.w, startH: item.h,
    };
    this.activeDragId = mode === 'drag' ? item.id : null;
    this.activeResizeId = mode === 'resize' ? item.id : null;
    const target = event.currentTarget as HTMLElement | null;
    this.interactionTarget = target ?? undefined;
    target?.setPointerCapture?.(event.pointerId);
  }

  private applyInteraction(clientX: number, clientY: number): void {
    if (!this.interaction) return;
    const item = (this.layouts[this.currentBp] ?? []).find((x) => x.id === this.interaction?.id);
    if (!item) return;
    const dx = Math.round((clientX - this.interaction.startClientX) / (this.cellW() + this.grid.gap));
    const dy = Math.round((clientY - this.interaction.startClientY) / (this.grid.rowHeight + this.grid.gap));
    if (this.interaction.mode === 'drag') {
      this.updateItem(item.id, { x: this.clamp(this.interaction.startX + dx, 0, this.cols - item.w), y: Math.max(0, this.interaction.startY + dy) });
    } else {
      const i = this.interaction;
      const corner = i.corner ?? 'se';
      let x = i.startX;
      let y = i.startY;
      let w = i.startW;
      let h = i.startH;

      if (corner === 'se') {
        const maxW = Math.min(item.maxW, this.cols - i.startX);
        w = this.clamp(i.startW + dx, item.minW, maxW);
        h = this.clamp(i.startH + dy, item.minH, item.maxH);
      } else if (corner === 'sw') {
        const maxW = Math.min(item.maxW, i.startX + i.startW);
        w = this.clamp(i.startW - dx, item.minW, maxW);
        x = i.startX + (i.startW - w);
        h = this.clamp(i.startH + dy, item.minH, item.maxH);
      } else if (corner === 'ne') {
        const maxW = Math.min(item.maxW, this.cols - i.startX);
        const maxH = Math.min(item.maxH, i.startY + i.startH);
        w = this.clamp(i.startW + dx, item.minW, maxW);
        h = this.clamp(i.startH - dy, item.minH, maxH);
        y = i.startY + (i.startH - h);
      } else {
        const maxW = Math.min(item.maxW, i.startX + i.startW);
        const maxH = Math.min(item.maxH, i.startY + i.startH);
        w = this.clamp(i.startW - dx, item.minW, maxW);
        h = this.clamp(i.startH - dy, item.minH, maxH);
        x = i.startX + (i.startW - w);
        y = i.startY + (i.startH - h);
      }

      this.updateItem(item.id, { x, y, w, h });
    }
  }

  private finishInteraction(persist: boolean): void {
    if (!this.interaction) return;
    try { this.interactionTarget?.releasePointerCapture?.(this.interaction.pointerId); } catch { }
    this.interaction = undefined; this.interactionTarget = undefined; this.activeDragId = null; this.activeResizeId = null; this.queued = null; this.raf = false;
    if (persist) this.writePersisted();
  }

  private updateItem(id: string, changes: Partial<GridItem>): void {
    const cur = this.layouts[this.currentBp] ?? [];
    const idx = cur.findIndex((x) => x.id === id);
    if (idx < 0) return;
    const next = cur.map((x) => ({ ...x }));
    next[idx] = this.sanitize({ ...next[idx], ...changes }, this.cols);
    this.layouts[this.currentBp] = this.pack(next, id, this.cols);
    this.updateRowsForResponsiveService();
  }

  private buildDefaultLayout(bp: string): GridItem[] {
    const cols = this.grid.columns[bp] ?? 12;
    const raw = this.widgets.map((rec) => {
      const w = this.clamp(this.spanToCols(this.span(rec.widget?.class, bp), cols), 1, cols);
      const minW = this.clamp(this.num(rec.widget?.minW, this.grid.defaultMinW), 1, cols);
      const minH = this.clamp(this.num(rec.widget?.minH, this.grid.defaultMinH), 1, this.grid.defaultMaxH);
      const h = this.clamp(
        this.heightToRows(this.resolveWidgetHeightForBreakpoint(rec.widget, bp)),
        minH,
        this.grid.defaultMaxH
      );
      return { id: rec.id, x: 0, y: 0, w: this.clamp(w, minW, cols), h, minW, maxW: this.clamp(this.num(rec.widget?.maxW, cols), minW, cols), minH, maxH: this.clamp(this.num(rec.widget?.maxH, this.grid.defaultMaxH), minH, this.grid.defaultMaxH), locked: !!rec.widget?.locked };
    });
    let cursorX = 0, cursorY = 0, rowH = 0;
    for (const item of raw) {
      if (cursorX + item.w > cols) { cursorX = 0; cursorY += rowH; rowH = 0; }
      item.x = cursorX; item.y = cursorY;
      cursorX += item.w; rowH = Math.max(rowH, item.h);
    }
    return this.pack(raw, null, cols);
  }

  private parseRawLayout(raw: Array<Record<string, unknown>>, bp: string): GridItem[] {
    const cols = this.grid.columns[bp] ?? 12;
    return raw.map((r) => {
      const id = String(r['id'] ?? r['i'] ?? '');
      const wrec = this.widgetMap.get(id)?.widget;
      const minW = this.clamp(this.num(r['minW'] ?? wrec?.minW, this.grid.defaultMinW), 1, cols);
      const minH = this.clamp(this.num(r['minH'] ?? wrec?.minH, this.grid.defaultMinH), 1, this.grid.defaultMaxH);
      const maxW = this.clamp(this.num(r['maxW'] ?? wrec?.maxW, cols), minW, cols);
      const maxH = this.clamp(this.num(r['maxH'] ?? wrec?.maxH, this.grid.defaultMaxH), minH, this.grid.defaultMaxH);
      return this.sanitize({ id, x: this.num(r['x'], 0), y: this.num(r['y'], 0), w: this.num(r['w'], this.grid.defaultW), h: this.num(r['h'], this.grid.defaultH), minW, maxW, minH, maxH, locked: !!(r['locked'] ?? wrec?.locked) }, cols);
    }).filter((x) => !!x.id && this.widgetMap.has(x.id));
  }

  private merge(
    base: GridItem[],
    overlay: GridItem[],
    bp: string,
    options?: { preserveBaseHeight?: boolean }
  ): GridItem[] {
    const cols = this.grid.columns[bp] ?? 12;
    const map = new Map(overlay.map((x) => [x.id, x]));
    return base.map((b) => {
      const merged = map.has(b.id) ? { ...b, ...map.get(b.id)! } : { ...b };
      if (options?.preserveBaseHeight) {
        merged.h = b.h;
      }
      return this.sanitize(merged, cols);
    });
  }

  private pack(items: GridItem[], activeId: string | null, cols: number): GridItem[] {
    const order = [...items].sort((a, b) => {
      if (activeId) { if (a.id === activeId && b.id !== activeId) return -1; if (b.id === activeId && a.id !== activeId) return 1; }
      return a.y - b.y || a.x - b.x;
    });
    const placed: GridItem[] = [];
    for (const it of order) { const c = this.sanitize(it, cols); while (this.collides(c, placed)) c.y += 1; placed.push(c); }
    const compacted: GridItem[] = [];
    for (const it of placed.sort((a, b) => a.y - b.y || a.x - b.x)) {
      const c = this.sanitize({ ...it }, cols);
      if (it.id !== activeId) while (c.y > 0 && !this.collides({ ...c, y: c.y - 1 }, compacted)) c.y -= 1;
      while (this.collides(c, compacted)) c.y += 1;
      compacted.push(c);
    }
    const map = new Map(compacted.map((x) => [x.id, x]));
    return this.widgets.map((w) => map.get(w.id)).filter((x): x is GridItem => !!x);
  }

  private collides(a: GridItem, list: GridItem[]): boolean {
    return list.some((b) => a.id !== b.id && a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y);
  }

  private sanitize(it: GridItem, cols: number): GridItem {
    const minW = this.clamp(it.minW, 1, cols), maxW = this.clamp(it.maxW, minW, cols);
    const minH = this.clamp(it.minH, 1, this.grid.defaultMaxH), maxH = this.clamp(it.maxH, minH, this.grid.defaultMaxH);
    const w = this.clamp(it.w, minW, maxW), h = this.clamp(it.h, minH, maxH);
    return { ...it, x: this.clamp(it.x, 0, cols - w), y: Math.max(0, Math.floor(it.y)), w, h, minW, maxW, minH, maxH, locked: !!it.locked };
  }

  private extractWidgets(content: any[]): WidgetRec[] {
    const out: WidgetRec[] = []; const count = new Map<string, number>();
    const visit = (n: any): void => {
      if (!n || typeof n !== 'object') return;
      if (n.type) {
        const base = String(n.widgetId ?? n.id ?? `widget-${out.length + 1}`).replace(/[^a-zA-Z0-9-_]/g, '-');
        const idx = (count.get(base) ?? 0) + 1; count.set(base, idx);
        out.push({ id: idx === 1 ? base : `${base}__${idx}`, widget: n });
        // A typed node is a widget config. Its own `columns`/`rows` can be
        // component-specific config, such as super-table columns, not dashboard
        // layout children. Stop here to avoid rendering those configs as blank
        // dashboard widgets.
        return;
      }
      if (Array.isArray(n.columns)) n.columns.forEach((x: any) => visit(x));
      if (Array.isArray(n.rows)) n.rows.forEach((x: any) => visit(x));
      if (Array.isArray(n.stats)) n.stats.forEach((x: any) => visit(x));
    };
    content.forEach((x) => visit(x));
    return out;
  }

  private readPersisted(): Record<string, GridItem[]> | null {
    if (!this.grid.persist) return null;
    // When a custom layout is active, the layout's row pattern is the source
    // of truth. Drag positions saved against a previous layout (or no layout)
    // would override the new column widths and leave cycled rows misaligned.
    if (this.layoutService.getActiveLayoutId()) return null;
    try {
      const raw = localStorage.getItem(this.persistKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.layouts ?? null;
    } catch { return null; }
  }

  private writePersisted(): void {
    if (!this.grid.persist) return;
    try { localStorage.setItem(this.persistKey(), JSON.stringify({ version: this.grid.version, layouts: this.layouts })); } catch { }
  }

  private persistKey(): string {
    if (this.grid.persistKey) return `dashboard-grid:${this.grid.persistKey}`;
    const routePath = this.route.snapshot.routeConfig?.path ?? this.route.snapshot.url.map((s) => s.path).join('/');
    return `dashboard-grid:${this.sourceKey || routePath || 'dashboard'}`;
  }

  private updateRowsForResponsiveService(): void {
    const max = (this.layouts[this.currentBp] ?? []).reduce((m, i) => Math.max(m, i.y + i.h), 0);
    this.responsiveLayoutService.setRowCount(Math.max(1, Math.ceil(max / Math.max(1, this.grid.defaultH))));
  }

  private queueCompactFitMeasurement(): void {
    if (this.screenFloating) {
      this.responsiveLayoutService.setCompactLayoutFitOverride(null);
      return;
    }
    if (this.compactFitRafId !== null) {
      cancelAnimationFrame(this.compactFitRafId);
    }
    this.compactFitRafId = requestAnimationFrame(() => {
      this.compactFitRafId = requestAnimationFrame(() => {
        this.compactFitRafId = null;
        this.measureCompactFit();
      });
    });
  }

  private measureCompactFit(): void {
    if (this.screenFloating) {
      this.responsiveLayoutService.setCompactLayoutFitOverride(null);
      return;
    }

    const contentEl = this.dashboardContentRef?.nativeElement;
    const hostEl = this.hostRef.nativeElement;
    if (!contentEl || !hostEl) return;

    const viewportHost =
      hostEl.closest('mat-sidenav-content, .mat-sidenav-content') as HTMLElement | null;
    const viewportBottom = viewportHost?.getBoundingClientRect().bottom ?? window.innerHeight;
    const contentTop = contentEl.getBoundingClientRect().top;
    const availableHeight = Math.max(0, Math.floor(viewportBottom - contentTop));
    const browserZoomScale = window.visualViewport?.scale ?? 1;
    const requiredHeight = this.responsiveLayoutService.getEstimatedCompactRequiredHeight();
    const currentlyCompact = !this.effectiveScreenFloating;
    const currentSnapshot = this.getCompactViewportSnapshot(requiredHeight);
    const heightMargin = currentlyCompact
      ? DashboardComponent.COMPACT_EXIT_MARGIN_PX
      : DashboardComponent.COMPACT_ENTER_MARGIN_PX;

    let fits =
      browserZoomScale <= DashboardComponent.COMPACT_BASELINE_BROWSER_ZOOM_MAX &&
      availableHeight >= (requiredHeight + heightMargin);

    if (!currentlyCompact && this.compactScrollLock) {
      const locked = this.compactScrollLockSnapshot;
      const viewportImproved = !!locked && (
        currentSnapshot.width >= locked.width + 48 ||
        currentSnapshot.height >= locked.height + 48 ||
        currentSnapshot.scale <= locked.scale - 0.05
      );
      const heightDemandImproved = !!locked &&
        currentSnapshot.requiredHeight <= Math.max(0, locked.requiredHeight - 24);

      if (!viewportImproved && !heightDemandImproved) {
        fits = false;
      }
    }

    if (currentlyCompact) {
      const gridEl = this.gridContainerRef?.nativeElement;
      const contentOverflow = contentEl.scrollHeight > (contentEl.clientHeight + 2);
      const gridOverflow = !!gridEl && gridEl.scrollHeight > (contentEl.clientHeight + 2);
      const minChartHeight = this.findSmallestChartHostHeight(gridEl);
      const chartTooShort =
        minChartHeight > 0 &&
        minChartHeight < this.responsiveLayoutService.getCompactMinimumChartHeight();

      if (contentOverflow || gridOverflow || chartTooShort) {
        fits = false;
      }
    }

    if (!fits) {
      this.compactScrollLock = true;
      this.compactScrollLockSnapshot = currentSnapshot;
    } else if (currentlyCompact || this.compactScrollLock) {
      this.compactScrollLock = false;
      this.compactScrollLockSnapshot = null;
    }

    this.responsiveLayoutService.setCompactLayoutFitOverride(fits);
  }

  private findSmallestChartHostHeight(gridEl?: HTMLElement): number {
    if (!gridEl) return 0;

    let minHeight = Number.POSITIVE_INFINITY;
    const chartHosts = gridEl.querySelectorAll('.card-body > div[id]');
    chartHosts.forEach((el) => {
      const height = (el as HTMLElement).clientHeight;
      if (height > 0 && height < minHeight) {
        minHeight = height;
      }
    });

    return Number.isFinite(minHeight) ? minHeight : 0;
  }

  private getCompactViewportSnapshot(requiredHeight = this.responsiveLayoutService.getEstimatedCompactRequiredHeight()): CompactViewportSnapshot {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      scale: window.visualViewport?.scale ?? 1,
      requiredHeight,
    };
  }

  private releaseCompactScrollLockIfViewportImproved(): void {
    if (this.screenFloating || !this.compactScrollLock || !this.compactScrollLockSnapshot) return;

    const current = this.getCompactViewportSnapshot();
    const locked = this.compactScrollLockSnapshot;
    const widthImproved = current.width >= locked.width + 48;
    const heightImproved = current.height >= locked.height + 48;
    const scaleImproved = current.scale <= locked.scale - 0.05;
    const requiredHeightImproved = current.requiredHeight <= Math.max(0, locked.requiredHeight - 24);

    if (widthImproved || heightImproved || scaleImproved || requiredHeightImproved) {
      this.compactScrollLock = false;
      this.compactScrollLockSnapshot = null;
    }
  }

  private resolveBp(width: number): string { return this.orderedBps.find((bp) => width >= (this.grid.breakpoints[bp] ?? 0)) ?? 'lg'; }
  private cellW(): number { return (Math.max(0, this.containerWidth - (this.cols - 1) * this.grid.gap)) / Math.max(1, this.cols); }
  private spanToCols(span: number, cols: number): number { return Math.round((span / 12) * cols) || cols; }
  private getGridItemChromeHeightPx(): number {
    const fontScale = Math.max(1, this.responsiveLayoutService.getFontScale());
    return Math.round(72 * fontScale);
  }
  private resolveWidgetHeightForBreakpoint(widget: any, bp: string): unknown {
    if (!widget || typeof widget !== 'object') {
      return widget;
    }

    if ((bp === 'xxs' || bp === 'xs' || bp === 'sm') && widget.heightSm != null && widget.heightSm !== '') {
      return widget.heightSm;
    }

    if (bp === 'md' && widget.heightMd != null && widget.heightMd !== '') {
      return widget.heightMd;
    }

    if ((bp === 'lg' || bp === 'xl') && widget.heightLg != null && widget.heightLg !== '') {
      return widget.heightLg;
    }

    if (widget.height != null && widget.height !== '') {
      return widget.height;
    }

    return widget?.chartConfig?.height ?? this.responsiveLayoutService.getDefaultWidgetHeight();
  }
  private heightToRows(height: unknown): number {
    const px = this.responsiveLayoutService.resolveHeightToPixels(height) ?? 0;
    const totalPx = px > 0 ? px + this.getGridItemChromeHeightPx() : 0;
    return px > 0
      ? Math.ceil((totalPx + this.grid.gap) / (this.grid.rowHeight + this.grid.gap))
      : this.grid.defaultH;
  }
  private span(cls: unknown, bp: string): number {
    if (typeof cls !== 'string') return 12;
    const chains: Record<string, string[]> = { xl: ['xl', 'lg', 'md', 'sm', ''], lg: ['lg', 'md', 'sm', ''], md: ['md', 'sm', ''], sm: ['sm', ''], xs: [''], xxs: [''] };
    for (const key of chains[bp] ?? ['']) {
      const re = key ? new RegExp(`(?:^|\\s)col-${key}-(\\d{1,2})(?=\\s|$)`) : /(?:^|\s)col-(\d{1,2})(?=\s|$)/;
      const m = cls.match(re);
      if (m?.[1]) return this.clamp(Number(m[1]), 1, 12);
    }
    return 12;
  }
  private normalizeType(type: unknown): string {
    return typeof type === 'string' ? type.trim().toLowerCase() : '';
  }
  private firstEnabledFlag(...values: unknown[]): boolean | null {
    for (const value of values) {
      const enabled = this.toEnabledFlag(value);
      if (enabled !== null) return enabled;
    }
    return null;
  }
  private toEnabledFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') return value;
    if (!value || typeof value !== 'object') return null;

    const cfg = value as Record<string, unknown>;
    if (typeof cfg['enabled'] === 'boolean') return cfg['enabled'];
    if (typeof cfg['enable'] === 'boolean') return cfg['enable'];
    if (typeof cfg['visible'] === 'boolean') return cfg['visible'];
    if (typeof cfg['show'] === 'boolean') return cfg['show'];

    return null;
  }
  private mapStrings(v: unknown): string[] {
    if (!Array.isArray(v)) return [];
    const out = new Set<string>();
    for (const item of v) {
      const normalized = this.normalizeType(item);
      if (normalized) out.add(normalized);
    }
    return [...out];
  }
  private mapNumbers(v: any, fallback: Record<string, number>): Record<string, number> {
    if (!v || typeof v !== 'object') return { ...fallback };
    const out: Record<string, number> = {};
    for (const [k, val] of Object.entries(v)) if (typeof val === 'number' && Number.isFinite(val) && val >= 0) out[k] = val;
    return Object.keys(out).length ? out : { ...fallback };
  }
  private num(v: unknown, fb: number): number {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : fb;
  }
  private clamp(v: number, min: number, max: number): number { return Math.min(max, Math.max(min, Math.round(v))); }
}
